import * as React from 'react';
import { useRouter } from 'next/router';
import { SearchResponse } from '@algolia/client-search';

import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { SearchBar, SearchBarProps } from '@packages/ui';
import { AlgoliaContext } from '@packages/services';
import { QuerySuggestions, debounce } from '@packages/common';
import { SideEffectsContext } from '../../context';

const ROUTES = getPublicRuntimeConfig('ROUTES');

interface SearchBarContainerProps extends SearchBarProps {
  testIdPrefix?: string;
}

export const SearchBarContainer: React.FC<SearchBarContainerProps> = ({
  isActive,
  toggleSearch,
  showBackButton,
  showSlogan,
  testIdPrefix,
}) => {
  const { querySuggestionSearch } = React.useContext(AlgoliaContext);
  const { searchSubmit: notifySearchSubmit, searchQuerySuggestionSubmit: notifySearchQuerySuggestionSubmit } =
    React.useContext(SideEffectsContext);

  const [query, setQuery] = React.useState('');
  const [response, setResponse] = React.useState<SearchResponse<QuerySuggestions> | null>(null);
  const { push } = useRouter();

  const searchSuggestions = React.useMemo(
    () => debounce((query: string) => querySuggestionSearch({ query }).then(setResponse), 200),
    [querySuggestionSearch],
  );

  React.useEffect(() => {
    searchSuggestions(query);
  }, [query, searchSuggestions]);

  React.useEffect(() => {
    if (!isActive) {
      setQuery('');
    }
  }, [isActive]);

  const handleSearch = async (query: string) => {
    setQuery('');
    push({ pathname: ROUTES.search, query: { query } });
  };

  const handleQuerySuggestionSubmit = async (querySuggestion: QuerySuggestions /*, position: number*/) => {
    // TODO: implement fire search events
    notifySearchQuerySuggestionSubmit(querySuggestion.query);
    await handleSearch(querySuggestion.query);
  };

  const handleQuerySubmit = async (event: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    notifySearchSubmit(query);
    await handleSearch(query);
  };

  return (
    <SearchBar
      testIdPrefix={testIdPrefix}
      isActive={isActive}
      toggleSearch={toggleSearch}
      showBackButton={showBackButton}
      showSlogan={showSlogan}
      query={query}
      onQueryChange={setQuery}
      querySuggestions={response ? response.hits : []}
      onQuerySuggestionSubmit={handleQuerySuggestionSubmit}
      onQuerySubmit={handleQuerySubmit}
    />
  );
};
